import { auth0ApiClient } from './auth0ApiClient'
import {
  MeetingRequest,
  CreateMeetingRequestData,
  MeetingRequestStatus,
  Meeting,
  AcceptMeetingRequestResponse,
  RescheduleMeetingResponse,
  ProposedTimeSlot,
  AddMeetingActionItemData
} from '@/types/meeting'

class MeetingService {
  private readonly baseUrl = '/meetings'

  /**
   * Create a new meeting request
   */
  async createMeetingRequest(data: CreateMeetingRequestData): Promise<MeetingRequest> {
    return auth0ApiClient.post<MeetingRequest>(`${this.baseUrl}/requests`, data)
  }

  /**
   * Get incoming and outgoing meeting requests for the authenticated user
   */
  async getMeetingRequests(status?: MeetingRequestStatus): Promise<MeetingRequest[]> {
    const queryString = status ? `?status=${status}` : ''
    return auth0ApiClient.get<MeetingRequest[]>(`${this.baseUrl}/requests${queryString}`)
  }

  /**
   * Accept a meeting request by selecting one of its proposed time slots
   */
  async acceptMeetingRequest(
    requestId: string,
    selectedTime: ProposedTimeSlot
  ): Promise<AcceptMeetingRequestResponse> {
    return auth0ApiClient.patch<AcceptMeetingRequestResponse>(
      `${this.baseUrl}/requests/${requestId}/accept`,
      { selectedTime }
    )
  }

  /**
   * Reject a meeting request with an optional reason
   */
  async rejectMeetingRequest(requestId: string, reason?: string): Promise<MeetingRequest> {
    return auth0ApiClient.patch<MeetingRequest>(
      `${this.baseUrl}/requests/${requestId}/reject`,
      reason ? { reason } : {}
    )
  }

  /**
   * Get the authenticated user's confirmed meetings, optionally filtered by
   * status (single value or comma-separated list, e.g. "completed,cancelled")
   */
  async getMeetings(status?: string): Promise<Meeting[]> {
    const queryString = status ? `?status=${status}` : ''
    return auth0ApiClient.get<Meeting[]>(`${this.baseUrl}${queryString}`)
  }

  /**
   * Get full detail for a single confirmed meeting
   */
  async getMeeting(meetingId: string): Promise<Meeting> {
    return auth0ApiClient.get<Meeting>(`${this.baseUrl}/${meetingId}`)
  }

  /**
   * Get all meetings in the same recurring series as the given meeting,
   * sorted by scheduled time
   */
  async getMeetingSeries(meetingId: string): Promise<Meeting[]> {
    return auth0ApiClient.get<Meeting[]>(`${this.baseUrl}/${meetingId}/series`)
  }

  /**
   * Cancel a meeting (creator only)
   */
  async cancelMeeting(meetingId: string): Promise<Meeting> {
    return auth0ApiClient.patch<Meeting>(`${this.baseUrl}/${meetingId}/cancel`, {})
  }

  /**
   * Reschedule a meeting by proposing new time slots (creator only)
   */
  async rescheduleMeeting(
    meetingId: string,
    proposedTimes: ProposedTimeSlot[]
  ): Promise<RescheduleMeetingResponse> {
    return auth0ApiClient.post<RescheduleMeetingResponse>(`${this.baseUrl}/${meetingId}/reschedule`, {
      proposedTimes
    })
  }

  /**
   * Toggle an agenda item's completed state
   */
  async toggleAgendaItem(meetingId: string, itemId: string): Promise<Meeting> {
    return auth0ApiClient.patch<Meeting>(`${this.baseUrl}/${meetingId}/agenda/${itemId}`, {})
  }

  /**
   * Toggle an action item's completed state
   */
  async toggleActionItem(meetingId: string, itemId: string): Promise<Meeting> {
    return auth0ApiClient.patch<Meeting>(`${this.baseUrl}/${meetingId}/actions/${itemId}`, {})
  }

  /**
   * Set or replace a meeting's notes
   */
  async updateMeetingNotes(meetingId: string, notes: string): Promise<Meeting> {
    return auth0ApiClient.patch<Meeting>(`${this.baseUrl}/${meetingId}/notes`, { notes })
  }

  /**
   * Add a new post-meeting action item
   */
  async addActionItem(meetingId: string, data: AddMeetingActionItemData): Promise<Meeting> {
    return auth0ApiClient.post<Meeting>(`${this.baseUrl}/${meetingId}/actions`, data)
  }
}

export const meetingService = new MeetingService()
